import React from 'react';
import styled from 'astroturf/react';

/**
 * The highlighted span inside an excerpt: the viewer's paper title in a
 * reference-list entry, or their name in a mention passage.
 */
const Mark = styled('mark')`
  padding: 1px 4px;
  border-radius: 2px;
  background: var(--brand-colors-expanded-colors-violet-600, #6b6997);
  color: var(--text-white, #ffffff);
`;

/**
 * Wraps occurrences of `needles` in `text` in <mark> elements, leaving the
 * text untouched when the needles are blank or absent. Matching is
 * case-insensitive because the citing paper's reference list may not spell a
 * title or a name the way we hold it.
 */
export function highlightText(
  text: string,
  needles?: string | Array<string | null | undefined>,
): React.ReactNode {
  const matches = (Array.isArray(needles) ? needles : [needles])
    .map((needle) => needle?.trim())
    .filter((needle): needle is string => Boolean(needle))
    .filter((needle, index, all) => all.indexOf(needle) === index)
    .flatMap((needle) => {
      const index = text.toLowerCase().indexOf(needle.toLowerCase());
      return index === -1 ? [] : [{ index, length: needle.length }];
    })
    .sort((left, right) => left.index - right.index);

  if (matches.length === 0) {
    return text;
  }

  const fragments: React.ReactNode[] = [];
  let cursor = 0;
  matches.forEach(({ index, length }) => {
    if (index < cursor) {
      return;
    }
    fragments.push(text.slice(cursor, index));
    fragments.push(
      <Mark key={index}>{text.slice(index, index + length)}</Mark>,
    );
    cursor = index + length;
  });
  fragments.push(text.slice(cursor));

  return fragments;
}
